/**
 * Canonical Safety Scan report view-model for the DISPLAYED report_id.
 * Reuses A2 / A4 / A6 / A9 presenters. Does not bind first_eligible_scan.
 * Does not invent rates, P/F/S, or Evaluation verdicts.
 */

import { formatHumanScanWindow } from '../acceptance/scanVerifyNavigation';
import { customerScanCameraLabel, customerScanConditionLabel } from '../acceptance/safetyScanProductPresentation';
import {
  presentCustomerResultDirection,
} from '../continuousSafety/conditionLifecyclePresentation';
import {
  presentFirstScanFinding,
  type FirstScanFindingView,
} from '../safetyCases/firstScanFindingPresenter';
import type { ScanControlRelationView } from '../safetyCases/scanControlTemporalRelation';
import type { ScanSigningView } from '../safetyCases/scanSigningPresenter';
import {
  resolveCanonicalScanWindow,
  storedWindowFromPersistedReport,
  type CanonicalScanWindow,
} from './canonicalScanWindow';
import {
  ENTIRE_CAMERA_VIEW_LABEL,
  presentStoredScanMeasurementBasis,
  type CanonicalStoredMeasurementBasisView,
} from './canonicalStoredMeasurementBasis';
import { presentScanVerificationWork } from './scanVerificationWork';

export const CANONICAL_REPORT_TITLE = 'Safety Scan';
export const EVALUATION_LIVES_ON_CASE =
  'Compared on the Safety Case, not in this Scan.';
export const RATE_UNAVAILABLE = 'Rate is not issuable for this Scan';
export const CLAIM_WITHHELD = 'This Scan does not have a usable claim.';

export type CanonicalReportLifecycle = 'Draft' | 'Verified' | 'Signed';

export type CanonicalEvidenceItem = {
  memberId: string;
  disposition: string;
  mediaAvailable: boolean;
  caption: string;
};

export type CanonicalScanLifecycleStripItem = {
  id: 'find' | 'alerts' | 'control' | 'since' | 'verify';
  label: string;
  state: string;
  detail: string;
};

export type CanonicalSafetyScanReportView = {
  reportId: string;
  displayedReportId: string;
  revisionLabel: string | null;
  firstEligibleReportId: string | null;
  usedFirstEligibleScan: boolean;
  conditionLabel: string;
  areaLabel: string;
  window: CanonicalScanWindow;
  windowLabel: string;
  lifecycle: CanonicalReportLifecycle;
  layerA: FirstScanFindingView;
  findingLabel: string;
  confirmedLabel: string;
  usableHoursLabel: string;
  rateLabel: string;
  rateIssuable: boolean;
  recurrenceLabel: string;
  whereLabel: string;
  siteLabel: string;
  cameraLabel: string;
  measurementScopeLabel: string;
  measurementBasis: CanonicalStoredMeasurementBasisView;
  whenLabel: string;
  concentrationLabel: string;
  howOftenLabel: string;
  verification: {
    total: number;
    reviewed: number;
    confirmed: number;
    cleared: number;
    unresolved: number;
    unreviewed: number;
    toConfirm: number;
    complete: boolean;
    lateArrivals: number;
    coverageSufficient: boolean | null;
    withheldReasons: string[];
  };
  controlRelation: ScanControlRelationView | null;
  caseHref: string | null;
  evaluationLinkLabel: string;
  limitations: string[];
  evidence: CanonicalEvidenceItem[];
  evidenceMissing: boolean;
  isCurrentScan: boolean;
  isLatestSigned: boolean;
  signedRecordHref: string | null;
  signingState: ScanSigningView['state'] | null;
  lifecycleStrip: CanonicalScanLifecycleStripItem[];
  comparisonCopy: string | null;
};

function text(value?: unknown): string {
  return String(value ?? '').trim();
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function lifecycleOf(args: {
  reportStatus?: string | null;
  verificationState?: string | null;
  complete?: boolean;
}): CanonicalReportLifecycle {
  const status = text(args.reportStatus).toLowerCase();
  const state = text(args.verificationState).toUpperCase();
  if (state === 'FINALIZED_IMMUTABLE' || status === 'finalized' || status === 'signed') {
    return 'Signed';
  }
  if (args.complete) return 'Verified';
  return 'Draft';
}

function presentConfirmedPerUsableHourRate(args: {
  issuable: boolean;
  storedRate: number | null | undefined;
  numerator: number | null | undefined;
  usableHours: number | null | undefined;
  fallback: string;
}): { label: string; issuable: boolean } {
  if (!args.issuable || args.storedRate == null || !Number.isFinite(Number(args.storedRate))) {
    return { label: args.fallback || RATE_UNAVAILABLE, issuable: false };
  }
  const storedRate = Number(args.storedRate);
  if (
    args.numerator != null
    && Number.isFinite(Number(args.numerator))
    && args.usableHours != null
    && Number.isFinite(Number(args.usableHours))
    && Number(args.usableHours) > 0
  ) {
    const implied = Number(args.numerator) / Number(args.usableHours);
    const tolerance = Math.max(0.05, Math.abs(implied) * 0.05);
    if (Math.abs(storedRate - implied) > tolerance) {
      return { label: RATE_UNAVAILABLE, issuable: false };
    }
  }
  return {
    label: `${storedRate} confirmed observations per usable hour`,
    issuable: true,
  };
}

function presentLifecycleStrip(args: {
  lifecycle: CanonicalReportLifecycle;
  controlRecorded: boolean;
  alertsLinked?: boolean;
  sinceAvailable?: boolean;
  comparisonAvailable?: boolean;
}): CanonicalScanLifecycleStripItem[] {
  return [
    {
      id: 'find',
      label: 'FIND',
      state: args.lifecycle,
      detail: 'Condition in this Scan',
    },
    {
      id: 'alerts',
      label: 'ALERTS',
      state: args.alertsLinked ? 'Linked' : 'Not linked',
      detail: 'Immediate / Pattern',
    },
    {
      id: 'control',
      label: 'CONTROL',
      state: args.controlRecorded ? 'Recorded' : 'Not recorded',
      detail: 'Relational only',
    },
    {
      id: 'since',
      label: 'SINCE',
      state: args.sinceAvailable ? 'Linked' : (args.controlRecorded ? 'Measurement pending' : 'Not started'),
      detail: 'Requires linked Control',
    },
    {
      id: 'verify',
      label: 'VERIFY',
      state: args.comparisonAvailable ? 'Comparison linked' : args.lifecycle,
      detail: 'Governed verification',
    },
  ];
}

export function presentCanonicalSafetyScanReport(input: {
  displayedReportId: string;
  firstEligibleReportId?: string | null;
  persistedReport?: Record<string, unknown> | null;
  caseScope?: Record<string, unknown> | null;
  locationLabel?: string | null;
  conditionRaw?: string | null;
  verification?: {
    report_id?: string | null;
    report_status?: string | null;
    state?: string | null;
    version?: number | string | null;
    window?: { start?: string | null; end?: string | null } | null;
    location_label?: string | null;
    scope?: { zone_id?: string | null; risk_type?: string | null; camera_id?: string | null } | null;
    progress?: {
      total?: number;
      unreviewed?: number;
      confirmed?: number;
      cleared?: number;
      unresolved?: number;
      complete?: boolean;
    } | null;
    late_arrivals?: { count?: number } | null;
  } | null;
  reconciliation?: {
    verification?: {
      total?: number;
      confirmed?: number;
      cleared?: number;
      unresolved?: number;
      unreviewed?: number;
      complete?: boolean;
    } | null;
    measurement?: {
      numerator?: { value?: number };
      denominator?: { usable_hours?: number | null; proven?: boolean };
      rate?: { value?: number | null; issuable?: boolean; withheld_reasons?: string[] };
      gates?: { coverage_sufficient?: boolean; rate_issuable?: boolean };
    } | null;
    late_arrivals?: { count?: number; claim_withheld_reason?: string | null } | null;
  } | null;
  controlRelation?: ScanControlRelationView | null;
  signing?: ScanSigningView | null;
  caseId?: string | null;
  timeZone?: string | null;
  evidence?: CanonicalEvidenceItem[];
  caseTimeWindow?: { start?: string | null; end?: string | null } | null;
  queueWindow?: { start?: string | null; end?: string | null } | null;
  reportOpenWindow?: { start?: string | null; end?: string | null } | null;
  workflowId?: string | null;
  lifecycleStory?: {
    alertsLinked?: boolean;
    controlRecorded?: boolean;
    sinceAvailable?: boolean;
    comparisonAvailable?: boolean;
    resultDirection?: string | null;
  } | null;
}): CanonicalSafetyScanReportView {
  const displayedReportId = text(input.displayedReportId);
  const firstEligibleReportId = text(input.firstEligibleReportId) || null;
  const persisted = asRecord(input.persistedReport);
  const stored = storedWindowFromPersistedReport(persisted as {
    window_start_utc?: string | null;
    window_end_utc?: string | null;
  });
  const window = resolveCanonicalScanWindow({
    storedWindowStart: stored.start,
    storedWindowEnd: stored.end,
    frozenWindowStart: input.verification?.window?.start,
    frozenWindowEnd: input.verification?.window?.end,
    caseTimeWindow: input.caseTimeWindow,
    queueWindow: input.queueWindow,
    reportOpenWindow: input.reportOpenWindow,
  });
  const work = presentScanVerificationWork(
    input.reconciliation?.verification,
    input.verification?.progress,
  );
  const total = work.total;
  const confirmed = work.confirmed;
  const cleared = work.cleared;
  const unresolved = work.unresolved;
  const unreviewed = work.unreviewed;
  const reviewed = work.reviewed;
  const complete = work.complete;
  const measurement = input.reconciliation?.measurement;
  const rateIssuable = Boolean(measurement?.rate?.issuable);
  const withheldReasons = Array.isArray(measurement?.rate?.withheld_reasons)
    ? measurement!.rate!.withheld_reasons!.filter(Boolean)
    : [];
  const lateArrivals = Number(
    input.reconciliation?.late_arrivals?.count ?? input.verification?.late_arrivals?.count ?? 0,
  );
  const layerA = presentFirstScanFinding({
    hasEligibleScan: Boolean(displayedReportId),
    reportId: displayedReportId,
    signed: lifecycleOf({
      reportStatus: input.verification?.report_status,
      verificationState: input.verification?.state,
      complete,
    }) === 'Signed',
    windowStart: window.start,
    windowEnd: window.end,
    timeZone: input.timeZone,
    report: persisted,
    caseScope: input.caseScope,
    locationLabel: input.locationLabel || input.verification?.location_label,
    progress: input.verification?.progress as Record<string, unknown> | undefined,
    riskType: text(input.verification?.scope?.risk_type) || text(input.conditionRaw),
    logicalScanCount: 1,
    coverageRefusesRate: measurement?.gates?.rate_issuable === false,
  });
  const lifecycle = lifecycleOf({
    reportStatus: input.verification?.report_status,
    verificationState: input.verification?.state,
    complete,
  });
  const conditionLabel = customerScanConditionLabel(
    input.conditionRaw
    || text(input.verification?.scope?.risk_type)
    || text(asRecord(input.caseScope).risk_type as string),
  ) || 'Safety condition';
  const usableHours = measurement?.denominator?.usable_hours;
  const usableHoursLabel = usableHours == null
    ? layerA.coverageLabel
    : `${usableHours} usable hours`;
  const confirmedCount = measurement?.numerator?.value;
  const confirmedLabel = confirmedCount == null
    ? layerA.observedLabel
    : `${confirmedCount} confirmed reviewed observation${confirmedCount === 1 ? '' : 's'}`;
  const presentedRate = presentConfirmedPerUsableHourRate({
    issuable: rateIssuable,
    storedRate: measurement?.rate?.value,
    numerator: confirmedCount,
    usableHours,
    fallback: layerA.rateLabel || RATE_UNAVAILABLE,
  });
  const rateLabel = presentedRate.label;
  const rateShownIssuable = presentedRate.issuable;
  const limitations: string[] = [];
  if (!rateIssuable) {
    if (withheldReasons.length) limitations.push(`Rate withheld: ${withheldReasons.join(', ')}`);
    else limitations.push(RATE_UNAVAILABLE);
  } else if (!rateShownIssuable) {
    limitations.push(RATE_UNAVAILABLE);
  }
  if (unresolved > 0) {
    limitations.push(`${unresolved} unresolved observation${unresolved === 1 ? '' : 's'} excluded from the confirmed numerator.`);
  }
  if (lateArrivals > 0) {
    const reason = text(input.reconciliation?.late_arrivals?.claim_withheld_reason);
    limitations.push(
      reason
        ? `Late arrivals: ${lateArrivals}. ${reason}`
        : `Late arrivals outside the frozen population: ${lateArrivals}.`,
    );
  }
  if (measurement?.gates?.coverage_sufficient === false) {
    limitations.push('Coverage is not sufficient for an issuable rate.');
  }
  if (unreviewed > 0 || !complete) {
    limitations.push(CLAIM_WITHHELD);
  }
  const evidence = Array.isArray(input.evidence) ? input.evidence : [];
  const signed = lifecycle === 'Signed';
  const measurementBasis = presentStoredScanMeasurementBasis({
    persisted: persisted,
    riskType: text(input.verification?.scope?.risk_type) || text(input.conditionRaw),
    workflowId: input.workflowId || text(asRecord(input.caseScope).workflow_id as string),
  });
  if (measurementBasis.source === 'missing' && measurementBasis.missingLabel) {
    limitations.push(measurementBasis.missingLabel);
  }
  const siteLabel = measurementBasis.siteId || text(persisted.site_id) || '';
  const cameraLabel = measurementBasis.cameraLabel
    || customerScanCameraLabel(
      text(
        measurementBasis.cameraId
        || input.verification?.scope?.camera_id
        || persisted.camera_id,
      ),
    )
    || text(measurementBasis.cameraId)
    || '';
  const measurementScopeLabel = measurementBasis.scopeLabel || ENTIRE_CAMERA_VIEW_LABEL;
  const structuredWhere = measurementBasis.source === 'stored'
    ? [measurementBasis.areaLabel, cameraLabel, measurementScopeLabel].filter(Boolean).join(' · ')
    : '';
  const where = structuredWhere || layerA.whereLabel;
  const revisionLabel = text(
    persisted.revision
    || persisted.report_revision
    || input.verification?.version,
  ) || null;
  return {
    reportId: displayedReportId,
    displayedReportId,
    revisionLabel,
    firstEligibleReportId,
    usedFirstEligibleScan: Boolean(
      firstEligibleReportId
      && firstEligibleReportId === displayedReportId,
    ),
    conditionLabel,
    areaLabel: measurementBasis.areaLabel || '',
    window,
    windowLabel: formatHumanScanWindow(window.start, window.end, input.timeZone),
    lifecycle,
    layerA,
    findingLabel: layerA.findingLabel,
    confirmedLabel,
    usableHoursLabel,
    rateLabel,
    rateIssuable: rateShownIssuable,
    recurrenceLabel: layerA.recurrenceLabel,
    whereLabel: where,
    siteLabel,
    cameraLabel,
    measurementScopeLabel,
    measurementBasis,
    whenLabel: layerA.whenLabel,
    concentrationLabel: layerA.concentrationLabel,
    howOftenLabel: confirmedLabel,
    verification: {
      total,
      reviewed,
      confirmed,
      cleared,
      unresolved,
      unreviewed,
      toConfirm: work.toConfirm,
      complete,
      lateArrivals,
      coverageSufficient: measurement?.gates?.coverage_sufficient ?? null,
      withheldReasons,
    },
    controlRelation: input.controlRelation || null,
    caseHref: text(input.caseId) ? `/acceptance/cases/${encodeURIComponent(text(input.caseId))}` : null,
    evaluationLinkLabel: EVALUATION_LIVES_ON_CASE,
    limitations,
    evidence,
    evidenceMissing: evidence.length > 0 && evidence.every((item) => !item.mediaAvailable),
    isCurrentScan: Boolean(input.signing?.isCurrentScan),
    isLatestSigned: Boolean(input.signing?.isLatestSigned),
    signedRecordHref: signed && displayedReportId
      ? `/acceptance/reports/records/${encodeURIComponent(displayedReportId)}`
      : null,
    signingState: input.signing?.state || null,
    lifecycleStrip: presentLifecycleStrip({
      lifecycle,
      controlRecorded: Boolean(
        input.lifecycleStory?.controlRecorded
        ?? (input.controlRelation && input.controlRelation.relation !== 'NOT_APPLICABLE'),
      ),
      alertsLinked: Boolean(input.lifecycleStory?.alertsLinked),
      sinceAvailable: Boolean(input.lifecycleStory?.sinceAvailable),
      comparisonAvailable: Boolean(input.lifecycleStory?.comparisonAvailable),
    }),
    comparisonCopy: presentCustomerResultDirection(
      input.lifecycleStory?.resultDirection,
    ),
  };
}
